import React, { useCallback, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { useHistory, useParams } from 'react-router-dom'
import { AppPage, UnderlineTabs } from 'src/components/app-ui'
import { LandingPagePreviewMain } from 'src/components/landing-page'
import usePortal from 'src/hooks/usePortal'
import useWorkspaces from 'src/hooks/useWorkspaces'
import { shopBrandingGetService } from 'src/api/shop'
import useShopLandingPage from './useShopLandingPage'
import NavigationTab from './navigation-tab'
import FooterTab from './footer-tab'
import SeoTab from './seo-tab'
import InAppTab from './in-app-tab'
import safeTrack from 'src/utils/safe-track'
import useStyles from './styles'

/** Portal tabs; `value` is the `:page` URL segment. */
const PORTAL_TABS = [
  { value: 'landing-page', label: 'Landing page' },
  { value: 'navigation', label: 'Navigation' },
  { value: 'footer', label: 'Footer' },
  { value: 'seo', label: 'SEO & sharing' },
  { value: 'in-app', label: 'In-app authentication' },
]

/**
 * The workspace portal. Its home is the "showcase" collection's landing page;
 * its other pages, menu, footer and SEO are the workspace's ShopLandingPage.
 */
const PortalPage = () => {
  const classes = useStyles()
  const history = useHistory()
  const { id, page } = useParams()
  const collection = useSelector(state => state.collection.collection)
  const { currentWorkspace } = useWorkspaces()
  const portalLinks = usePortal(collection?.shopKey)
  const activeTab = PORTAL_TABS.some(tab => tab.value === page)
    ? page
    : 'landing-page'
  // Landing page and SEO read the showcase (home) landing page; Navigation and
  // Footer the workspace portal, fetched the first time one of them opens.
  const {
    portal,
    update,
    createPage,
    updatePage,
    deletePage,
  } = useShopLandingPage(activeTab === 'navigation' || activeTab === 'footer')
  // Page URLs as learners see them, without the protocol.
  const host = (portalLinks.home || '').replace(/^https?:\/\//, '')
  const shopName = currentWorkspace?.shopName || 'Your academy'
  // Workspace › Branding logo: the header and footer use it unless they have their own.
  const [workspaceLogo, setWorkspaceLogo] = useState(null)
  useEffect(() => {
    safeTrack('viewed_portal_tab', { tab: activeTab })
  }, [activeTab])
  useEffect(() => {
    let cancelled = false
    shopBrandingGetService()
      .then(response => !cancelled && setWorkspaceLogo(response?.data?.logo))
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [])

  /** Opens a portal page in the landing page editor (home: the showcase's). */
  const openEditor = useCallback(
    target => {
      const editor = `/collection/${id}/landing-page/editor`
      if (target.type === 'home') {
        history.push(editor)
        return
      }
      history.push(`${editor}?portalPage=${target.id}`, {
        portalPage: target,
        returnTo: `/collection/${id}/${activeTab}`,
      })
    },
    [history, id, activeTab],
  )

  const shared = {
    portal,
    update,
    createPage,
    host,
    shopName,
    workspaceLogo,
    openEditor,
  }

  return (
    <AppPage
      title="Portal"
      subtitle="The public site where learners browse and open your courses."
    >
      <div className={classes.page}>
        <UnderlineTabs
          tabs={PORTAL_TABS}
          value={activeTab}
          onChange={value => history.push(`/collection/${id}/${value}`)}
        />
        {activeTab === 'landing-page' && <LandingPagePreviewMain />}
        {portal && activeTab === 'navigation' && (
          <NavigationTab
            {...shared}
            updatePage={updatePage}
            deletePage={deletePage}
          />
        )}
        {portal && activeTab === 'footer' && <FooterTab {...shared} />}
        {activeTab === 'seo' && collection && (
          <SeoTab collection={collection} />
        )}
        {activeTab === 'in-app' && collection && (
          <InAppTab collection={collection} />
        )}
      </div>
    </AppPage>
  )
}

export default PortalPage
